'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef } from 'react';
import {
  useNotificationPreferences,
  usePushStatus,
  useUpdateNotificationPreference,
  useUpdatePriceAlertPush,
} from '@/lib/api/notifications/hooks';
import { DialogWalletStepUp } from '@/lib/components/Account/Settings/DialogWalletStepUp';
import { useTelegramConnect } from '@/lib/hooks/useTelegramConnect';
import { LABEL_CLASS, Z_OVERLAY_MAX } from './constants';
import { DeliveryMethodCard } from './DeliveryMethodCard';

export function PriceAlertDeliverySection() {
  const tf = useTranslations('priceAlerts.form');
  const tSettings = useTranslations('account.settings');

  const preferencesQuery = useNotificationPreferences();
  const { linked, username, connectViaDeepLink, isConnecting, isStatusLoading, stepUp } =
    useTelegramConnect();

  const priceAlertPref = preferencesQuery.data?.find((p) => p.type === 'PRICE_ALERT');
  const telegramEnabled = Boolean(priceAlertPref?.telegramEnabled);

  const updatePreference = useUpdateNotificationPreference();
  // Set when the connect flow starts from this card — once the bot Start links
  // Telegram (poll flips `linked`), enable price-alert delivery so the card
  // turns active instead of stalling at connected-but-disabled.
  const enableOnLinkRef = useRef(false);

  const connect = useCallback(() => {
    enableOnLinkRef.current = true;
    connectViaDeepLink();
  }, [connectViaDeepLink]);

  useEffect(() => {
    if (!enableOnLinkRef.current || !linked) return;
    enableOnLinkRef.current = false;
    updatePreference.mutate({ type: 'PRICE_ALERT', telegramEnabled: true });
  }, [linked, updatePreference]);

  const pushStatusQuery = usePushStatus();
  const updatePush = useUpdatePriceAlertPush();
  const hasDevice = Boolean(pushStatusQuery.data?.hasActiveDevice);
  const pushEnabled = Boolean(pushStatusQuery.data?.priceAlertPushEnabled);

  const togglePush = useCallback(() => {
    updatePush.mutate(!pushEnabled);
  }, [updatePush, pushEnabled]);

  return (
    <div className="flex flex-col gap-2">
      <span className={LABEL_CLASS}>{tf('delivery')}</span>
      <div className="grid grid-cols-1 gap-4 min-[800px]:grid-cols-2">
        <DeliveryMethodCard
          method="telegram"
          active={linked && telegramEnabled}
          connected={linked}
          username={username}
          onConnect={connect}
          isConnecting={isConnecting}
          isStatusLoading={isStatusLoading}
        />
        <DeliveryMethodCard
          method="push"
          active={hasDevice && pushEnabled}
          hasDevice={hasDevice}
          onToggle={togglePush}
          isToggling={updatePush.isPending}
          isStatusLoading={pushStatusQuery.isLoading}
        />
      </div>

      {/* 2FA step-up — only when attaching a new Telegram identity with 2FA on;
          collects the TOTP before the bot deep-link opens. */}
      {stepUp.target && (
        <DialogWalletStepUp
          isOpen
          setIsOpen={(open) => {
            if (!open) stepUp.close();
          }}
          title={tSettings('enableTelegramTitle')}
          confirmLabel={tSettings('enableNotifications')}
          require2fa
          isSubmitting={stepUp.isSubmitting}
          hasError={stepUp.hasError}
          onConfirm={stepUp.submit}
          style={{ zIndex: Z_OVERLAY_MAX }}
        />
      )}
    </div>
  );
}
